import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { MaskedField } from "../components/MaskedField";
import { KeyRound, ShieldCheck, History, RotateCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const PROCESSORS = ["Citidan", "Adyen", "PayPal", "Authorize.Net", "Braintree", "Paystack", "Stripe", "Flutterwave"];

export default function ApiSettings() {
  const [credential, setCredential] = useState(null);
  const [archives, setArchives] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ processor: "Citidan", api_key: "", endpoint_url: "", secret_key: "", encryption_key: "" });

  const load = () =>
    api
      .get("/credentials")
      .then((res) => {
        setCredential(res.data.credential);
        setArchives(res.data.archives || []);
      })
      .catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/credentials", form);
      toast.success(res.data.rotated ? "Key rotated — old version archived" : "Credentials encrypted & saved");
      setForm({ processor: "Citidan", api_key: "", endpoint_url: "", secret_key: "", encryption_key: "" });
      setEditing(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs font-mono uppercase tracking-wider text-primary">Payment Processor</div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">API Credentials</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Store processor keys once. They are encrypted on save and displayed masked forever after.
        </p>
      </div>

      {credential && !editing ? (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="credential-view">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <KeyRound className="h-5 w-5" />
              </div>
              <div>
                <div className="font-medium">{credential.processor}</div>
                <div className="text-xs text-muted-foreground">
                  Updated {new Date(credential.updated_at).toLocaleString()}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="replace-credential-btn">
              <RotateCw className="mr-1.5 h-4 w-4" /> Rotate Key
            </Button>
          </div>
          <div className="space-y-4">
            <MaskedField label="API Key" value={credential.api_key_mask} testid="cred-api-key" />
            <MaskedField label="Secret Key" value={credential.secret_key_mask} testid="cred-secret-key" />
            <MaskedField label="Endpoint URL" value={credential.endpoint_url} mono={false} testid="cred-endpoint" />
            {credential.has_encryption_key && (
              <MaskedField label="Encryption Key" value={credential.encryption_key_mask} testid="cred-encryption-key" />
            )}
          </div>
          {archives.length > 0 && (
            <div className="mt-6 border-t border-border pt-4" data-testid="credential-archives">
              <div className="mb-3 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <History className="h-3.5 w-3.5" /> Rotation history ({archives.length})
              </div>
              <div className="space-y-2">
                {archives.map((a) => (
                  <div key={a.id} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                    <span className="font-mono masked-dots text-muted-foreground">{a.mask}</span>
                    <span className="text-xs text-muted-foreground">retired {new Date(a.retired_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={save} className="rounded-xl border border-border bg-card p-6" data-testid="credential-form">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Processor</Label>
              <Select value={form.processor} onValueChange={(v) => setForm({ ...form, processor: v })}>
                <SelectTrigger data-testid="processor-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROCESSORS.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>API Key</Label>
              <Input data-testid="api-key-input" value={form.api_key} onChange={set("api_key")} required placeholder="pk_live_..." />
            </div>
            <div className="space-y-1.5">
              <Label>Secret Key</Label>
              <Input data-testid="secret-key-input" value={form.secret_key} onChange={set("secret_key")} required placeholder="sk_live_..." />
            </div>
            <div className="space-y-1.5">
              <Label>Endpoint URL</Label>
              <Input data-testid="endpoint-input" value={form.endpoint_url} onChange={set("endpoint_url")} required placeholder="https://api.processor.com/v1" />
            </div>
            <div className="space-y-1.5">
              <Label>Encryption Key <span className="text-muted-foreground">(optional — required for Citidan card charges)</span></Label>
              <Input data-testid="encryption-key-input" value={form.encryption_key} onChange={set("encryption_key")} placeholder="CITISECK_TEST… 3DES key" />
            </div>
            <div className="flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Values are encrypted server-side and never returned raw.
            </div>
            <div className="flex gap-3">
              <Button type="submit" disabled={saving} data-testid="save-credential-btn">
                {saving ? "Encrypting…" : "Encrypt & Save"}
              </Button>
              {credential && (
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
